import { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

interface PressPublication {
  name: string;
  logoStyle: string;
  href: string;
}

interface PressQuote {
  quote: string;
  publication: string;
  feature: string;
}

interface CredentialBadge {
  label: string;
  isPlaceholder?: boolean;
}

interface PressSectionProps {
  variant?: 'publications' | 'credentials';
  eyebrow?: string;
  headline?: string;
  publications?: PressPublication[];
  featuredQuote?: PressQuote | null;
  credentials?: CredentialBadge[];
  placeholderCount?: number;
}

const defaultPublications: PressPublication[] = [
  {
    name: 'Psychology Today',
    logoStyle: 'font-heading text-lg md:text-xl tracking-tight italic',
    href: '#',
  },
  {
    name: 'Forbes',
    logoStyle: 'font-heading text-xl md:text-2xl font-bold tracking-tighter uppercase',
    href: '#',
  },
  {
    name: 'Well+Good',
    logoStyle: 'font-body text-lg md:text-xl font-medium tracking-tight',
    href: '#',
  },
  {
    name: 'mindbodygreen',
    logoStyle: 'font-body text-base md:text-lg font-semibold lowercase tracking-tight',
    href: '#',
  },
  {
    name: 'Oprah Daily',
    logoStyle: 'font-heading text-lg md:text-xl tracking-wide',
    href: '#',
  },
  {
    name: 'The Everygirl',
    logoStyle: 'font-body text-base md:text-lg font-light italic tracking-wider',
    href: '#',
  },
];

const defaultQuote: PressQuote = {
  quote:
    'Mandie Ozee is redefining what it means to heal. Her HOME is not just coaching, it is a full sensory unlearning of everything women have been taught to perform.',
  publication: 'Well+Good',
  feature: 'The Women Reshaping Mental Wellness in 2025',
};

const defaultCredentials: CredentialBadge[] = [
  { label: 'Licensed Clinical Psychologist' },
  { label: 'Member, National Association of Clinical Psychologists (NACP)' },
  { label: 'Conference Speaker' },
  { label: '', isPlaceholder: true },
  { label: '', isPlaceholder: true },
];

export default function PressSection({
  variant = 'publications',
  eyebrow: eyebrowProp,
  headline: headlineProp,
  publications = defaultPublications,
  featuredQuote = defaultQuote,
  credentials = defaultCredentials,
}: PressSectionProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const logosRef = useRef<HTMLDivElement>(null);
  const quoteRef = useRef<HTMLDivElement>(null);
  const badgesRef = useRef<HTMLDivElement>(null);
  const [badgesVisible, setBadgesVisible] = useState(false);

  const eyebrow = eyebrowProp ?? (variant === 'credentials' ? 'Credentials and Affiliations' : 'Featured in');
  const headline = headlineProp ?? (variant === 'credentials' ? undefined : 'Recognised by the publications that shape the conversation.');

  // Intersection Observer for CSS-driven badge animations
  useEffect(() => {
    if (variant !== 'credentials') return;
    const el = badgesRef.current;
    if (!el) return;

    let disconnected = false;
    const fallbackTimer = setTimeout(() => {
      if (!disconnected) setBadgesVisible(true);
    }, 3000);

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setBadgesVisible(true);
          observer.disconnect();
          disconnected = true;
          clearTimeout(fallbackTimer);
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    observer.observe(el);
    return () => {
      observer.disconnect();
      disconnected = true;
      clearTimeout(fallbackTimer);
    };
  }, [variant]);

  // GSAP for header + publications/quote
  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        headerRef.current,
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: 'top 70%',
          },
        }
      );

      if (variant !== 'credentials') {
        if (logosRef.current) {
          const logoEls = logosRef.current.children;
          gsap.fromTo(
            logoEls,
            { opacity: 0, y: 20 },
            {
              opacity: 1,
              y: 0,
              duration: 0.7,
              stagger: 0.1,
              scrollTrigger: {
                trigger: logosRef.current,
                start: 'top 85%',
              },
            }
          );
        }

        if (quoteRef.current) {
          gsap.fromTo(
            quoteRef.current,
            { opacity: 0, y: 25 },
            {
              opacity: 1,
              y: 0,
              duration: 0.9,
              delay: 0.4,
              scrollTrigger: {
                trigger: quoteRef.current,
                start: 'top 85%',
              },
            }
          );
        }
      }
    }, sectionRef);

    return () => ctx.revert();
  }, [variant]);

  return (
    <section
      ref={sectionRef}
      className="bg-[#FDFAF8] py-16 md:py-28 lg:py-32"
    >
      <div className="w-full px-4 md:px-8 lg:px-12 max-w-6xl mx-auto">
        {/* Header */}
        <div ref={headerRef} className="text-center mb-14">
          <p className="text-[#C9A84C] text-xs font-body tracking-[0.25em] uppercase mb-6">
            {eyebrow}
          </p>
          {headline && (
            <h2 className="font-heading text-xl md:text-3xl lg:text-5xl text-[#1A1A1A] leading-tight max-w-3xl mx-auto">
              {headline}
            </h2>
          )}
        </div>

        {variant === 'credentials' ? (
          <>
            {/* Credentials Badges */}
            <div
              ref={badgesRef}
              className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-3 md:gap-4 mb-16"
            >
              {credentials.map((cred, idx) => (
                cred.isPlaceholder ? (
                  <span
                    key={`placeholder-${idx}`}
                    className="w-full sm:w-auto inline-block px-5 py-2.5 border border-dashed border-[#C9A84C]/40 rounded-full font-body text-sm text-[#C9A84C]/40 text-center sm:whitespace-nowrap sm:min-w-[140px]"
                    style={badgesVisible ? { animation: `fadeInUp 0.8s ease-out ${idx * 0.12}s both` } : { opacity: 0 }}
                  >
                    &nbsp;
                  </span>
                ) : (
                  <span
                    key={cred.label}
                    className="w-full sm:w-auto inline-block px-5 py-2.5 border border-[#C9A84C] rounded-full font-body text-sm text-[#C9A84C] text-center sm:whitespace-nowrap"
                    style={badgesVisible ? { animation: `fadeInUp 0.8s ease-out ${idx * 0.12}s both` } : { opacity: 0 }}
                  >
                    {cred.label}
                  </span>
                )
              ))}
            </div>

            {/* Gold Divider */}
            <div className="w-16 h-px bg-[#C9A84C] mx-auto" />
          </>
        ) : (
          <>
            {/* Publication Logos Grid */}
            <div
              ref={logosRef}
              className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5 md:gap-10 items-center justify-items-center mb-16"
            >
              {publications.length > 0 ? (
                publications.map((pub) => (
                  <a
                    key={pub.name}
                    href={pub.href}
                    rel="nofollow"
                    className={`${pub.logoStyle} text-[#4A4A4A] hover:text-[#C9A84C] transition-colors duration-300 whitespace-nowrap cursor-pointer`}
                  >
                    {pub.name}
                  </a>
                ))
              ) : (
                <div className="col-span-full text-center">
                  <p className="font-body text-sm text-foreground-500 italic">
                    Publications and features coming soon.
                  </p>
                </div>
              )}
            </div>

            {/* Gold Divider */}
            <div className="w-16 h-px bg-[#C9A84C] mx-auto mb-12" />

            {/* Featured Quote */}
            {featuredQuote ? (
              <div ref={quoteRef} className="max-w-2xl mx-auto text-center">
                <p className="font-quote text-lg md:text-xl italic text-[#4A4A4A] leading-relaxed mb-6">
                  {featuredQuote.quote}
                </p>
                <p className="font-body text-sm text-[#C9A84C]">
                  &mdash; {featuredQuote.publication},{' '}
                  <span className="italic">{featuredQuote.feature}</span>
                </p>
              </div>
            ) : (
              <div className="max-w-2xl mx-auto text-center">
                <p className="font-body text-sm text-foreground-500 italic">
                  Press features coming soon.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}